import cx from 'classnames';
import AtMentionMembers from 'Components/AtMentionMembers';
import {
  AttachmentsContainer,
  AttachmentsRef,
} from 'Components/Chat/ChatFooter/AttachmentsContainer';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { useAttachments } from 'Components/Chat/hooks/attachments/useAttachments';
import ContentEditable from 'Components/ContentEditable';
import { EmojiButton } from 'Components/shared/EmojiButton';
import { Icon } from 'Components/shared/Icon';
import { IconButton } from 'Components/shared/IconButton';
import { MENTION_PREFIX } from 'Constants/enums';
import { KLIPY_API_KEY, SHOW_FILE_UPLOAD } from 'Constants/env';
import { COMMUNICATOR_GIFS } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import useCheckLongMessage from 'Hooks/longMessage/useCheckLongMessage';
import { isEmpty, isNumber } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import type {
  IMessageDocument,
  IMessageGifPayload,
  IMessageReferences,
} from 'Models/MessageModel';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { usePersonStore } from 'Modules/person/index.store';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router';
import { Popup } from 'semantic-ui-react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import {
  ANALYTICS_EVENT_CHAT_GIF_MESSAGE_SENT,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import {
  findMentionInsertData,
  focusContentEditable,
  getSelectionRange,
  insertEmoji,
  placeCursorAtEndOfElement,
  placeCursorAtSpecificPosition,
} from 'Utils/inputNodeUtils';
import { keyIsAlphanumeric, keyIsValidFilterInput } from 'Utils/keyboardInput';
import {
  handleAlphanumericWhenMentionListOpen,
  handleAtSymbol,
  handleBackspaceDeleteWhenMentionListOpen,
  handleEnterKey,
  handleEscapeKey,
  handleTabRightArrowSpacebarWhenMentionListOpen,
  handleUpArrowEditOnEmptyInput,
  handleUpDownLeftArrowsWhenMentionListOpen,
} from 'Utils/mentionKeyHandlers';
import {
  preserveSpacesForTurndown,
  convertNbspToSpace,
} from 'Utils/preserveSpacesForTurndown';
import { resolveConversationPath } from 'Utils/routeNav';
import { routeChecks } from 'Utils/routeUtils';
import { turndownSvc } from 'Utils/turndownSvc';
import { cn } from '~/lib/utils';
import { GifPicker } from '~/modules/ui/gif';
import { Styled } from './index.styles';
import { MessageBanner } from './MessageBanner';
import { MessageBannerProps } from './MessageBanner/index.types';

export const MessageInputArea: React.FC = observer(() => {
  const { participant, pusher, person, message, ui, conversation } =
    React.useContext<RootStoreProps>(MobXProviderContext);
  const { setView, replyMessage, setReplyMessage } =
    React.useContext(ChatLayoutContext);

  const { selectedContacts } = useConversationStore();

  const location = useLocation();
  const navigate = useNavigate();
  const { conversationId = '' } = useParams();

  const { isAvailablePhoneNumber } = usePersonStore();

  const [canReceiveSMS, setCanReceiveSMS] = React.useState(true);
  const [userSMSErrorMessage, setUserSMSErrorMessage] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [showUploadInfoBanner, setShowUploadInfoBanner] = useState(false);
  const [returnPressed, setReturnPressed] = useState(false);
  const { messageIsTooLong, checkIsMessageTooLong, resetMessageIsTooLong } =
    useCheckLongMessage();

  const { isAdHocGroupCreation } = useConversationStore.getState();

  const contentEditableRef = useRef<ContentEditable>(null);

  const {
    addAttachmentsViaEvent,
    getAttachmentsCount,
    hasUploadErrors,
    hasUploadsInProgress,
    uploadedS3ObjectsMap,
    resetAttachments,
  } = useAttachments();

  const gifFeatureFlags = useFeatureFlags([COMMUNICATOR_GIFS]);
  const isGifsFeatureEnabled = Boolean(gifFeatureFlags[COMMUNICATOR_GIFS]);
  const filePickerRef = useRef<AttachmentsRef>(null);

  useEffect(() => {
    if (conversationId === '0' || location.pathname === '/chat/adHocGroup') {
      return;
    }
    getSMS();
  }, []);

  useEffect(() => {
    if (conversationId === '0' || location.pathname === '/chat/adHocGroup') {
      return;
    }

    setCanReceiveSMS(true);
    getSMS();

    if (!ui.cursorPosition) {
      placeCursorAtEndOfElement(contentEditableRef.current?.htmlEl);
      ui.setCursorPosition(contentEditableRef.current?.htmlEl.innerText.length);
    }
  }, [conversationId]);

  useEffect(() => {
    replyMessage && focusContentEditable(contentEditableRef.current);
  }, [replyMessage]);

  useEffect(() => {
    if (returnPressed && hasUploadsInProgress) {
      setShowUploadInfoBanner(true);
    } else {
      setShowUploadInfoBanner(false);
      setReturnPressed(false);
    }
  }, [returnPressed, hasUploadsInProgress]);

  const showSendError = ui.userSmsCantSendError.get(conversationId) === 'false';

  const isGroupChat =
    conversation.CurrentConversation?.grouping === 'Channel' ||
    conversation.CurrentConversation?.grouping === 'Group';

  const isSMSConversation = !!participant
    .selectOtherParticipants(conversationId)
    ?.some(({ phone }) => phone);

  const emojiPickerOpenState =
    ui.getEmojiPickerState().isEmojiPickerOpen &&
    !ui.getEmojiPickerState().isEditingEmoji;

  let isDeadkeyPressed = false;
  let previousKeyWasDeadKey = false;

  /**
   * Check if the message is too long, and clean the input if it is the right size.
   * @param text The text to validate.
   * @returns Returns true if message is too long, and false if it isn't.
   */
  const checkLongMessageAndClean = useCallback(
    (text: string) => {
      if (checkIsMessageTooLong(text)) {
        return true;
      }

      setReplyMessage(null);
      resetAttachments();
      message.setMessageDraftRaw(conversationId, '');
      message.setMessageDraftHtml(conversationId, '');
      cleanContentEditable();

      return false;
    },
    [
      checkIsMessageTooLong,
      conversationId,
      message,
      setReplyMessage,
      resetAttachments,
    ]
  );

  const handleClick = async (e) => {
    // If online, send the message and reset the input
    await handleEnterKey(
      e,
      pusher.isOnline,
      conversationId,
      message.createMessageMentionListOpen,
      message.createMessageMentionSelectedParticipantId,
      insertMentionAtCursor,
      participant.selectOtherParticipantPersons,
      sendMessage
    );
    setView('');
    focusContentEditable(contentEditableRef.current, undefined, true);
  };

  const deliverOutboundMessage = useCallback(
    async ({
      rawMessageContent,
      attachmentDocuments,
      replyForPost,
      gif,
    }: {
      rawMessageContent: string;
      attachmentDocuments: IMessageDocument[] | undefined;
      replyForPost: IMessageReferences | null;
      gif?: IMessageGifPayload | null;
    }): Promise<boolean> => {
      let sendAsSMS =
        participant.conversationMessagesSMS.get(conversationId) || false;

      let targetConversationId = conversationId;
      let currentConversation = conversation.CurrentConversation;
      let previewConversationId = null;

      const isNewConversation = routeChecks.isNewConversationPage();
      if (isNewConversation) {
        previewConversationId =
          useConversationStore.getState().previewConversationId;
        if (previewConversationId) {
          targetConversationId = previewConversationId;
          if (!currentConversation) {
            const previewConv = conversation.selectConversationById(
              previewConversationId
            );
            if (previewConv && previewConv.state === 'fulfilled') {
              currentConversation = previewConv.value.data;
            }
          }
        }
      }

      const otherParticipants =
        participant.selectOtherParticipants(targetConversationId);
      if (otherParticipants?.length === 1) {
        const otherParticipant = otherParticipants[0];
        if (otherParticipant.phone && !otherParticipant.personId) {
          sendAsSMS = true;
        } else if (otherParticipant.personId) {
          const otherPerson = person.selectPersonValueById(
            otherParticipant.personId
          );
          if (!otherPerson) {
            sendAsSMS = true;
          }
        }
      }

      if (location.pathname.includes('adHocGroup') || isAdHocGroupCreation) {
        if (
          conversation.adHocGroupParticipants.every(
            (p) => p instanceof ContactModelBase
          )
        ) {
          sendAsSMS = true;
        }

        if (!gif && checkLongMessageAndClean(rawMessageContent)) {
          return false;
        }

        void conversation.createConversationFromAdHoc(
          conversation.adHocGroupParticipants.map((contact) => {
            if (
              contact instanceof ContactModelV2 &&
              contact.source === 'Bhive'
            ) {
              return contact.sourceId;
            }

            return contact.id;
          }),
          rawMessageContent,
          sendAsSMS,
          attachmentDocuments,
          gif ?? undefined
        );
        return true;
      }

      if (gif?.id) {
        if (!currentConversation) {
          console.error('No conversation available, cannot send message');
          return false;
        }

        const postStatus = await message.createMessagePost(
          targetConversationId,
          null,
          null,
          currentConversation.grouping,
          attachmentDocuments,
          replyForPost ?? undefined,
          gif
        );

        if (!postStatus) {
          return false;
        }
      } else {
        const messageContent =
          !rawMessageContent.length && !replyForPost
            ? null
            : {
                text: rawMessageContent,
                text_v2: `${
                  replyForPost ? `@ref_{${replyForPost.id}} ` : ''
                }${rawMessageContent}`,
              };

        if (
          checkLongMessageAndClean(
            sendAsSMS ? rawMessageContent : messageContent?.text_v2
          )
        ) {
          return false;
        }

        if (!currentConversation) {
          console.error('No conversation available, cannot send message');
          return false;
        }

        const postStatus = await message.createMessagePost(
          targetConversationId,
          sendAsSMS ? null : messageContent,
          sendAsSMS ? { text: rawMessageContent } : null,
          currentConversation.grouping,
          attachmentDocuments,
          replyForPost ?? undefined
        );

        if (!postStatus) {
          return false;
        }
      }

      if (isNewConversation && previewConversationId) {
        void navigate(`/chat/conversations/${previewConversationId}/menu`);
      }

      return true;
    },
    [
      checkLongMessageAndClean,
      conversation,
      conversationId,
      isAdHocGroupCreation,
      location.pathname,
      message,
      navigate,
      participant,
      person,
    ]
  );

  const sendStandaloneGifMessage = useCallback(
    async (gifId: string) => {
      const sent = await deliverOutboundMessage({
        rawMessageContent: '',
        attachmentDocuments: undefined,
        replyForPost: null,
        gif: { id: gifId },
      });

      if (!sent) {
        return;
      }

      pushToGTMDataLayer(ANALYTICS_EVENT_CHAT_GIF_MESSAGE_SENT, {
        conversationId,
      });

      setReplyMessage(null);

      const objDiv = document.querySelector('.context-content-items-list');
      if (objDiv) {
        objDiv.scrollTop = objDiv.scrollHeight;
      }
    },
    [conversationId, deliverOutboundMessage, setReplyMessage]
  );

  const sendMessage = async () => {
    const rawMessageContent = message.getMessageDraftRaw(conversationId);
    const replyForPost = replyMessage;

    if (rawMessageContent?.length > 0 || getAttachmentsCount) {
      const sent = await deliverOutboundMessage({
        rawMessageContent,
        attachmentDocuments: uploadedS3ObjectsMap,
        replyForPost,
      });

      if (!sent) {
        return;
      }

      if (
        message
          .getMessageDraftHtml(conversationId)
          .includes('class="input-mention"')
      ) {
        pushToGTMDataLayer('mention', { conversationId });
      }
      const objDiv = document.querySelector('.context-content-items-list');
      if (objDiv) {
        objDiv.scrollTop = objDiv.scrollHeight;
      }
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    isDeadkeyPressed = false;
  };

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (previousKeyWasDeadKey && !isDeadkeyPressed) {
      previousKeyWasDeadKey = false;
      return;
    }

    if (e.key === 'Dead') {
      isDeadkeyPressed = true;
      previousKeyWasDeadKey = true;
    }

    // Enter
    if (e.key === 'Enter') {
      // prevent repeated submission if user holds down the enter key
      if (e.repeat === true) {
        e.preventDefault();
        return;
      }

      if (hasUploadsInProgress || hasUploadErrors) {
        setReturnPressed(true);
        e.preventDefault();
        return;
      }

      await handleEnterKey(
        e,
        pusher.isOnline,
        conversationId,
        message.createMessageMentionListOpen,
        message.createMessageMentionSelectedParticipantId,
        insertMentionAtCursor,
        participant.selectOtherParticipantPersons,
        sendMessage
      );

      setView('');
      focusContentEditable(contentEditableRef.current, undefined, true);
    }

    if (e.key === 'Escape') {
      handleEscapeKey(e, ui.setEmojiPickerState);
    }

    // Up arrow without shift key, while the new message input is empty, and the mention list is not open
    if (
      e.key === 'ArrowUp' &&
      !e.shiftKey &&
      (isGroupChat || !message.createMessageMentionListOpen) &&
      isEmpty(message.getMessageDraftRaw(conversationId))
    ) {
      const newestOwn = message.newestMessageOwnedByUserId;
      if (!newestOwn?.gif?.id) {
        handleUpArrowEditOnEmptyInput(
          e,
          conversation.CurrentConversation,
          participant.selectOtherParticipantPersons,
          newestOwn,
          message.setEditMessageDraftRaw,
          message.setEditMessageDraftHtml,
          message.isEditingMessageId,
          message.setIsEditingMessageId
        );
      }
    }

    // @ symbol (shift + 2), pop up the mentions list
    if (isGroupChat && e.key === '@' && !isSMSConversation) {
      // Use actual innerText from ContentEditable (not trimmed messageDraftRaw)
      const actualText = contentEditableRef.current?.htmlEl?.innerText || '';
      handleAtSymbol(
        e,
        contentEditableRef.current.selectionRange,
        actualText,
        message.createMessageMentionListOpen,
        message.setCreateMessageMentionListOpen,
        message.setCreateMessageMentionSelectedParticipantId,
        ui.setEmojiPickerState
      );
    }
    // Handle Up/Down arrows, Backspace, and Delete when the Mention list is open
    if (isGroupChat && message.createMessageMentionListOpen) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft'].includes(e.key)) {
        handleUpDownLeftArrowsWhenMentionListOpen(
          e,
          participant.selectFilteredOtherParticipantPersonsInCurrentConversation,
          message.createMessageMentionSelectedParticipantId,
          message.setCreateMessageMentionSelectedParticipantId,
          message.setCreateMessageMentionListOpen,
          message.createMessageMentionFilter
        );
      } else if (['ArrowRight', 'Tab', ' '].includes(e.key)) {
        handleTabRightArrowSpacebarWhenMentionListOpen(
          e,
          message.createMessageMentionSelectedParticipantId,
          participant.selectOtherParticipantPersons,
          conversationId,
          insertMentionAtCursor
        );
      } else if (['Backspace', 'Delete'].includes(e.key)) {
        handleBackspaceDeleteWhenMentionListOpen(
          e,
          contentEditableRef.current.selectionRange,
          message.getMessageDraftHtml(conversationId),
          message.createMessageMentionListOpen,
          message.setCreateMessageMentionListOpen,
          message.createMessageMentionFilter,
          message.setCreateMessageMentionFilter,
          participant.selectFilteredOtherParticipantPersonsInCurrentConversation,
          message.setCreateMessageMentionSelectedParticipantId
        );
      } else if (keyIsAlphanumeric(e)) {
        handleAlphanumericWhenMentionListOpen(
          e,
          message.createMessageMentionListOpen,
          message.createMessageMentionFilter,
          message.setCreateMessageMentionFilter,
          participant.selectFilteredOtherParticipantPersonsInCurrentConversation,
          message.setCreateMessageMentionSelectedParticipantId
        );
      }
      // Any invalid inputs should close the Mention list to avoid auto-complete desync
      else if (!keyIsValidFilterInput(e)) {
        message.setCreateMessageMentionListOpen(false);
      }
    }
  };

  const getSMS = () => {
    if (
      isEmpty(participant.selectLoggedInUserParticipant(conversationId)) ||
      isEmpty(conversation.CurrentConversation)
    )
      return;

    if (
      isSMSConversation &&
      conversation.CurrentConversation?.grouping === 'OneOnOne'
    ) {
      const otherParticipants =
        participant.selectOtherParticipants(conversationId);
      // SMS messages can only be sent to OneOnOne Chats that have number Or they are Pro Users
      // Check to see if the other participant is a standard or a pro user.
      // otherPerson is null if it's just a phone number
      if (otherParticipants?.length <= 0) return;

      if (!otherParticipants[0].personId) return setErrIfUserCantReceiveSMS();
      const otherPerson = person.selectPersonValueById(
        otherParticipants[0].personId || parseInt(otherParticipants[0].id)
      );
      if (otherPerson === null) return setErrIfUserCantReceiveSMS();
    }
  };

  const setErrIfUserCantReceiveSMS = () => {
    const loggedInUser = usePersonStore.getState();
    // Check for when messaging a non-participant (mobile number)
    // Check if logged in user has SMS capability/direct number
    if (!loggedInUser.canReceiveSMS) {
      setCanReceiveSMS(false);
      setUserSMSErrorMessage(true);
    }
  };

  const onHover = () => !canReceiveSMS && setIsHover(true);
  const offHover = () => setIsHover(false);

  const toggleShowEmojis = (open: boolean) => {
    if (open) {
      ui.setEmojiPickerState({
        isEmojiPickerOpen: true,
        isEditingEmoji: false,
      });
      message.setCreateMessageMentionListOpen(false);
      if (contentEditableRef.current?.htmlEl) {
        const contentEditableRefSelection = getSelectionRange(
          contentEditableRef.current?.htmlEl
        );
        // Focus and move to end if there is no selection (unfocused) in the input
        if (
          contentEditableRefSelection.start === 0 &&
          contentEditableRefSelection.end === 0
        ) {
          focusContentEditable(contentEditableRef.current);
          placeCursorAtEndOfElement(contentEditableRef.current?.htmlEl);
        }
      }
    } else {
      ui.setEmojiPickerState({
        isEmojiPickerOpen: false,
        isEditingEmoji: false,
      });
    }
  };

  const updateMessage = (inputValue: string) => {
    if (!isDeadkeyPressed) {
      resetMessageIsTooLong();

      if (inputValue) {
        inputValue = inputValue
          .replace(/(<p>|<br>)/gi, '\n')
          .replace(/<\/p>/gi, '')
          .replace(/&amp;/gi, '&');
      }

      let rawResultValue = inputValue.trim();
      if (inputValue.includes('class="input-mention"')) {
        const htmlWithPreservedSpaces = preserveSpacesForTurndown(
          inputValue.replace(/\n/gi, '<br>')
        );
        rawResultValue = turndownSvc.turndown(htmlWithPreservedSpaces);
        rawResultValue = convertNbspToSpace(rawResultValue);
      }
      rawResultValue = rawResultValue
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>');

      message.setMessageDraftRaw(conversationId, rawResultValue);
      message.setMessageDraftHtml(
        conversationId,
        rawResultValue ? inputValue : ''
      );
    }
  };

  const insertMentionAtCursor = (prefix: MENTION_PREFIX, value: string) => {
    // Use actual DOM HTML for HTML manipulation (preserves mention divs)
    const actualHtml = contentEditableRef.current?.htmlEl?.innerHTML || '';

    const { htmlStart, htmlEnd } = contentEditableRef.current.selectionRange;

    let messageDraftHtml: string;

    if (prefix === '@pr') {
      const personId = parseInt(value, 10);
      if (isNumber(personId)) {
        const participantPersons =
          participant.selectParticipantPersons(conversationId); // Can't be null at this point (render function doesn't include Mentions unless it is not null)
        const mentionedPerson = participantPersons?.find(
          (pp) => pp.person.sourceId === personId.toString()
        );

        const htmlInsertData = findMentionInsertData(actualHtml, htmlStart);
        messageDraftHtml =
          actualHtml.substring(0, htmlInsertData.insertIndex) +
          `${htmlInsertData.prependWith}&nbsp;<div class="input-mention" mention-target="${prefix}${value}" contenteditable="false">@${mentionedPerson.person.firstName} ${mentionedPerson.person.lastName}</div>&nbsp;` +
          actualHtml.substring(htmlEnd);
      } else {
        return; // Can't find person, don't insert
      }
    } else {
      const htmlInsertData = findMentionInsertData(actualHtml, htmlStart);
      messageDraftHtml =
        actualHtml.substring(0, htmlInsertData.insertIndex) +
        `${htmlInsertData.prependWith}&nbsp;<div class="input-mention" mention-target="${prefix}${value}" contenteditable="false">${prefix}${value}</div>&nbsp;` +
        actualHtml.substring(htmlEnd);
    }

    // Update HTML first
    message.setMessageDraftHtml(conversationId, messageDraftHtml);
    message.setCreateMessageMentionListOpen(false);

    // Generate raw text from HTML using turndownSvc to ensure @pr{number} format is preserved
    // This correctly converts all mention divs back to @pr{number} format
    const htmlWithPreservedSpaces = preserveSpacesForTurndown(
      messageDraftHtml.replace(/\n/gi, '<br>')
    );
    let rawFromHtml = turndownSvc.turndown(htmlWithPreservedSpaces);
    rawFromHtml = convertNbspToSpace(rawFromHtml);
    message.setMessageDraftRaw(conversationId, rawFromHtml);

    const startPosition =
      contentEditableRef.current.selectionRange.start +
      value.length +
      prefix.length +
      1;
    contentEditableRef.current.selectionRange = {
      ...contentEditableRef.current.selectionRange,
      start: startPosition,
      end: startPosition,
    };
  };

  const fileImportEvent = async (e: any): Promise<void> => {
    await addAttachmentsViaEvent(e);
  };

  const invokeFilePicker = () => {
    filePickerRef.current?.triggerFilePicker();
    focusContentEditable(contentEditableRef.current);
  };

  const cleanContentEditable = () => {
    contentEditableRef.current.lastHtml = '';
  };

  const isFileUploadActive =
    SHOW_FILE_UPLOAD && (!isSMSConversation || isAvailablePhoneNumber);

  const messageTooLongBanner: MessageBannerProps = {
    canClose: false,
    text: 'It seems that your message is too long. Please shorten it.',
  };

  const uploadInfoBanner: MessageBannerProps = {
    icon: 'info',
    text: `Sit tight, we're uploading your file${
      getAttachmentsCount > 1 ? 's' : ''
    }`,
  };

  const showMessageBanner = messageIsTooLong || showUploadInfoBanner;

  const getDisabledButtonTitle = useCallback(() => {
    if (messageIsTooLong) return 'Shorten your message first';
    if (hasUploadsInProgress) return "Sit tight, we're uploading your file";
    if (hasUploadErrors)
      return 'Error uploading files to this conversation, try uploading the files again';
    if (isSMSConversation && !isAvailablePhoneNumber && !!getAttachmentsCount) {
      return "MMS feature isn't enabled on your account, please remove any attached file to send your message";
    }
    return '';
  }, [
    messageIsTooLong,
    hasUploadsInProgress,
    hasUploadErrors,
    isSMSConversation,
    isAvailablePhoneNumber,
    getAttachmentsCount,
  ]);

  const isNewConversationFormEmpty =
    routeChecks.isNewConversationPage() && selectedContacts?.length === 0;

  // Combine the existing canReceiveSMS check with the new disabled prop
  const isInputDisabled = !canReceiveSMS || isNewConversationFormEmpty;

  return (
    <Styled.MessageInputArea
      id="message-input"
      onClick={(e) => e.stopPropagation()}
      className={cn(
        isNewConversationFormEmpty && 'opacity-50 pointer-events-none'
      )}
    >
      {!canReceiveSMS && isHover && (
        <Styled.MessageInfo onMouseEnter={onHover} onMouseLeave={offHover}>
          {!userSMSErrorMessage
            ? 'The user that you are trying to message does not currently have a number that can receive SMS.'
            : 'To enable SMS, please request a phone number from your account admin.'}
        </Styled.MessageInfo>
      )}
      {showSendError && (
        <Styled.MessageInfo onMouseEnter={onHover} onMouseLeave={offHover}>
          The user that you are trying to message does not currently have a
          number that can receive SMS.
        </Styled.MessageInfo>
      )}
      <Styled.MessageBar largeMargin={showMessageBanner}>
        <Styled.InputAndBannerWrapper>
          <MessageBanner
            {...(messageIsTooLong
              ? messageTooLongBanner
              : showUploadInfoBanner
              ? uploadInfoBanner
              : {})}
            isVisible={showMessageBanner}
          />

          <Styled.InputWrapper
            className={cn(
              'message-input-area-toolbar',
              (isSMSConversation || !isGifsFeatureEnabled || !KLIPY_API_KEY) &&
                'message-input-toolbar--no-gif'
            )}
            onMouseEnter={onHover}
            onMouseLeave={offHover}
            onKeyDown={(e) =>
              e.key === 'Escape' && handleEscapeKey(e, ui.setEmojiPickerState)
            }
          >
            {replyMessage && (
              <Styled.ReplyBox
                reference={replyMessage}
                onRemoveClick={() => setReplyMessage(null)}
              />
            )}
            <ContentEditable
              {...{ fileImportEvent, onKeyDown, onKeyUp }}
              ref={contentEditableRef}
              className={cx('ce-new-message _lr-hide', {
                'ce-disabled': !canReceiveSMS,
              })}
              placeholder="Send a Message"
              html={message.getMessageDraftHtml(conversationId)}
              onChange={(e) => updateMessage(e.target.value)}
              disabled={isInputDisabled}
              onBlur={() =>
                !message.isEditingMessageId &&
                ui.setCursorPosition(
                  contentEditableRef.current.selectionRange.start
                )
              }
              onFocus={() =>
                placeCursorAtSpecificPosition(
                  contentEditableRef.current.htmlEl,
                  ui.cursorPosition
                )
              }
              updateMessage={updateMessage}
            />
            {conversationId !== '0' &&
              conversation.CurrentConversation?.id === conversationId &&
              isGroupChat &&
              !isSMSConversation &&
              participant.selectOtherParticipantPersons(conversationId) !==
                null && ( // Only possible to Mention if the list of others has loaded
                <AtMentionMembers
                  {...{ conversationId, insertMentionAtCursor, onKeyDown }}
                  contentEditableRef={contentEditableRef}
                  getEmojiPickerState={ui.getEmojiPickerState}
                  key="at-mention-members"
                  loggedInPersonId={person.loggedInPersonId}
                  mentionListOpen={message.createMessageMentionListOpen}
                  messageMentionFilter={message.createMessageMentionFilter}
                  resolveConversationLinkPath={(path: string) =>
                    resolveConversationPath(location.pathname, path)
                  }
                  selectedMentionParticipantId={
                    message.createMessageMentionSelectedParticipantId
                  }
                  selectFilteredOtherParticipantPersonsInCurrentConversation={
                    participant.selectFilteredOtherParticipantPersonsInCurrentConversation
                  }
                  selectParticipantPersons={
                    participant.selectParticipantPersons
                  }
                  selectPersonPresenceStatus={ui.selectPersonPresenceStatus}
                  selectUnreadCounts={ui.selectConversationUnreadCounts}
                  setConversationAndTotalUnreadCount={
                    ui.setConversationAndTotalUnreadCount
                  }
                  updateMyLastReadMessage={participant.updateMyLastReadMessage}
                  setMentionListOpen={message.setCreateMessageMentionListOpen}
                  setMessageMentionFilter={
                    message.setCreateMessageMentionFilter
                  }
                  setSelectedMentionParticipantId={
                    message.setCreateMessageMentionSelectedParticipantId
                  }
                  setEmojiPickerState={ui.setEmojiPickerState}
                />
              )}
            {isFileUploadActive ? (
              <div className="attach-wrapper">
                <IconButton
                  onClick={invokeFilePicker}
                  icon="attachment"
                  size="tiny"
                  testid="chat-button-add-attachment"
                />
              </div>
            ) : (
              <Popup
                trigger={
                  <div className="attach-wrapper disable-attachment">
                    <Icon icon="attachment" size="tiny" />
                  </div>
                }
                content="You are unable to send files - MMS feature is not yet available."
                position="top center"
              />
            )}
            <EmojiButton
              isOpen={emojiPickerOpenState}
              placement="top-end"
              onOpen={() => toggleShowEmojis(true)}
              onClose={() => toggleShowEmojis(false)}
              onChooseEmoji={(emoji) =>
                insertEmoji(emoji, contentEditableRef.current)
              }
            />

            {isGifsFeatureEnabled && !isSMSConversation && KLIPY_API_KEY && (
              <GifPicker onSelectGif={sendStandaloneGifMessage} />
            )}
          </Styled.InputWrapper>
        </Styled.InputAndBannerWrapper>
        <Styled.SubmitIconButton
          icon="send"
          onClick={handleClick}
          size="medium"
          testid="messageInputArea-buttonSubmitMessage"
          variant="primary"
          disabled={{
            condition:
              isNewConversationFormEmpty ||
              messageIsTooLong ||
              hasUploadsInProgress ||
              hasUploadErrors ||
              (isSMSConversation &&
                !isAvailablePhoneNumber &&
                !!getAttachmentsCount),
            title: getDisabledButtonTitle(),
          }}
        />
      </Styled.MessageBar>
      {SHOW_FILE_UPLOAD && (
        <React.Suspense fallback={<div></div>}>
          <AttachmentsContainer ref={filePickerRef} />
        </React.Suspense>
      )}
    </Styled.MessageInputArea>
  );
});
